/**
 * value-control — wspólna logika kontrolki wartości operacji „Ustaw pole" dla „Zmień zbiorczo"
 * (`BulkChangeModal`) i edytora zapisanej akcji (`ActionEditorModal`): dobór kontrolki do typu
 * pola, wykluczenia pól, których nie wolno ustawiać zbiorczo, oraz konwersja wartości
 * „data z godziną" (lokalny `datetime-local` ⇄ ISO 8601 ze strefą wysyłany do API).
 */

import { isoToLocalDateTimeInput, localDateTimeInputToIso } from '@/lib/local-date';

export type ValueControlKind = 'text' | 'number' | 'bool' | 'date' | 'datetime' | 'select';

export function valueControlKind(fieldType: string): ValueControlKind {
  const ft = fieldType.toLowerCase();
  if (['integer', 'numeric', 'number', 'decimal', 'currency'].includes(ft)) return 'number';
  if (['boolean', 'checkbox'].includes(ft)) return 'bool';
  if (ft === 'date') return 'date';
  if (['datetime', 'timestamp'].includes(ft)) return 'datetime';
  if (['select', 'multiselect'].includes(ft)) return 'select';
  return 'text';
}

/**
 * Pola, których „Ustaw pole" nie obsługuje niezależnie od kartoteki: referencje (kolumna uuid —
 * zmiana omijałaby reguły rekordu, serwer też je odrzuca) oraz pola tylko-do-odczytu.
 */
export function isExcludedFromBulkSet(field: { fieldType: string; readOnly?: boolean }): boolean {
  return field.fieldType.toLowerCase() === 'reference' || field.readOnly === true;
}

/** Wartość z draftu → wartość do API. Data z godziną → ISO; null gdy niepoprawna/pusta. */
export function draftValueToApi(kind: ValueControlKind, value: string): string | null {
  if (kind === 'datetime') return localDateTimeInputToIso(value);
  return value;
}

/** Wartość zapisana w definicji akcji → wartość kontrolki (data z godziną: ISO → czas lokalny). */
export function storedValueToDraft(kind: ValueControlKind, stored: string): string {
  if (kind !== 'datetime') return stored;
  // Akcje sprzed wprowadzenia strefy mogą trzymać datę bez strefy — serwer taką odrzuca, więc
  // czyścimy pole (użytkownik wybiera datę z godziną na nowo), zamiast zgadywać strefę.
  return /(Z|[+-]\d{2}:\d{2})$/.test(stored) ? isoToLocalDateTimeInput(stored) : '';
}
